import type {
  GetV1WorkspacesWorkspaceidApplicationsQueryParamsSortEnumKey,
  WorkspaceResponseV2,
} from '@emma/api_client/types';
import { Button, Icon, Tooltip } from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { useMemo, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { useApplications } from '@/hooks/application';
import {
  buildFilterParams,
  type FilterConfig,
  serverTableParams,
} from '@/hooks/common/serverTableParams';
import { useCanWrite } from '@/hooks/workspace/use_workspace_access';
import { paths } from '@/utility/global_paths';
import { getApplicationTableColumns } from '../common/table/column_definitions/Application';
import { TableStoreProvider, useTableStore } from '../common/table/store/Context';
import { Table } from '../common/table/Table';
import { CreateApplicationDialog } from './CreateApplicationDialog';

type Props = {
  workspace: WorkspaceResponseV2;
};

const FILTER_DEBOUNCE_MS = 300;

const FILTERS: FilterConfig = {
  id: 'idIn',
  name: 'nameLike',
  createdAt: ['createdAfter', 'createdBefore'],
  updatedAt: ['updatedAfter', 'updatedBefore'],
};

const ApplicationTableContent = (props: Props) => {
  const columns = useMemo(
    () => getApplicationTableColumns(props.workspace.id),
    [props.workspace.id]
  );
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const canWrite = useCanWrite(props.workspace);
  const { pagination, sorting, filters } = useTableStore((s) => s);
  const [debouncedFilters] = useDebounceValue(filters, FILTER_DEBOUNCE_MS);

  const queryParams = {
    ...serverTableParams<GetV1WorkspacesWorkspaceidApplicationsQueryParamsSortEnumKey>(
      pagination,
      sorting
    ),
    ...buildFilterParams(debouncedFilters, FILTERS),
  };

  const { data, isLoading, mutate, isValidating } = useApplications(
    props.workspace.id,
    queryParams
  );

  const totalItems = data?.itemCount || 0;
  const pageCount = Math.ceil(totalItems / pagination.pageSize);

  return (
    <>
      <Table
        addButton={
          canWrite
            ? () => (
                <Tooltip label="Create a new application" placement="left">
                  <Button
                    borderRightRadius="0px"
                    leftIcon={<Icon as={PlusBold} />}
                    onClick={() => setIsCreateDialogOpen(true)}
                    size="sm"
                    variant="outline"
                  >
                    Create
                  </Button>
                </Tooltip>
              )
            : undefined
        }
        columns={columns}
        data={data?.applications || []}
        isLoading={isLoading}
        isValidating={isValidating}
        refresh={mutate}
        rowLink={({ id }) => paths.applicationById(props.workspace.id, id)}
        serverSide={{ pageCount, rowCount: totalItems }}
        title="Applications"
      />
      <CreateApplicationDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        workspaceId={props.workspace.id}
      />
    </>
  );
};

export const ApplicationTable = (props: Props) => (
  <TableStoreProvider
    initialSorting={[{ desc: true, id: 'id' }]}
    key={`Applications-${props.workspace.id}`}
    name={`Applications-${props.workspace.id}`}
  >
    <ApplicationTableContent {...props} />
  </TableStoreProvider>
);
